// Etiquetas, formato y reglas puras de las pantallas de inventario (conteo a
// ciegas, sincronización de stock y bitácora de ajustes). Sin dependencias de
// Vue: se prueban en inventoryFormat.spec.js.
//
// Las cantidades llegan del backend como texto con 4 decimales ("1.6850"); se
// muestran con hasta 3, que es lo que el usuario puede declarar (litros, kilos).

const RESULTS = {
    BALANCED: { label: 'Cuadrado', severity: 'success' },
    OVERAGE: { label: 'Sobrante', severity: 'info' },
    SHORTAGE: { label: 'Faltante', severity: 'danger' },
    NOT_COUNTED: { label: 'No contado', severity: 'secondary' },
    DIFFERENCES: { label: 'Con diferencias', severity: 'warning' },
};

const COUNT_STATUS = {
    OPEN: { label: 'Vigente', severity: 'success' },
    APPLIED: { label: 'Aplicado', severity: 'info' },
    VOIDED: { label: 'Anulado', severity: 'danger' },
};

// El motivo decide la cuenta contable: la merma natural va contra el costo de
// ventas; el consumo normal de un insumo periódico (sal, limpieza), a la cuenta de
// consumo de su categoría; los demás, a faltantes o sobrantes.
export const REASONS = [
    { value: 'MERMA', label: 'Merma natural', hint: 'Espuma, limpieza de líneas, evaporación' },
    { value: 'CONSUMO', label: 'Consumo normal', hint: 'Lo que se usó de un insumo de conteo periódico', periodicOnly: true },
    { value: 'ROTURA', label: 'Rotura o daño', hint: 'Se rompió o se dañó' },
    { value: 'DIFERENCIA', label: 'Diferencia sin explicar', hint: 'No se sabe por qué' },
    { value: 'OTRO', label: 'Otro', hint: 'Explíquelo en el detalle' },
];

/** Motivos que admite un producto: "Consumo normal" solo en los periódicos. */
export function reasonsFor(periodic) {
    return REASONS.filter(r => !r.periodicOnly || periodic);
}

/** Motivo con que arranca la sincronización: lo que baja solo en un insumo
 *  periódico es su consumo; en el resto, la merma natural. */
export function defaultSyncReason(periodic) {
    return periodic ? 'CONSUMO' : 'MERMA';
}

const SOURCES = {
    SYNC: 'Sincronización',
    COUNT: 'Conteo',
    MANUAL: 'Ajuste manual',
};

const SKIP_REASONS = {
    EXCLUDED: 'Excluida por el administrador',
    SUPERSEDED: 'Ya se ajustó después del conteo',
    NEGATIVE_RESULT: 'Dejaría el stock en negativo',
    PRODUCT_UNAVAILABLE: 'El producto ya no maneja inventario',
    BALANCED: 'Sin diferencia',
    NOT_COUNTED: 'No contado',
};

export const MAX_DECIMALS = 3;

export function resultLabel(code) {
    return (RESULTS[code] && RESULTS[code].label) || code || '—';
}

export function resultSeverity(code) {
    return (RESULTS[code] && RESULTS[code].severity) || 'info';
}

export function statusLabel(code) {
    return (COUNT_STATUS[code] && COUNT_STATUS[code].label) || code || '—';
}

export function statusSeverity(code) {
    return (COUNT_STATUS[code] && COUNT_STATUS[code].severity) || 'info';
}

export function reasonLabel(code) {
    const reason = REASONS.find(r => r.value === code);
    return reason ? reason.label : (code || '—');
}

export function sourceLabel(code) {
    return SOURCES[code] || code || '—';
}

export function skipReasonLabel(code) {
    return SKIP_REASONS[code] || code || '';
}

function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

/** Cantidad para mostrar: hasta 3 decimales, sin ceros de relleno. */
export function formatQty(value) {
    const n = toNumber(value);
    if (n === null) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: MAX_DECIMALS });
}

/** Diferencia con signo explícito: "+2", "-1.685", "0". */
export function formatSignedQty(value) {
    const n = toNumber(value);
    if (n === null) return '—';
    const text = formatQty(Math.abs(n));
    if (n > 0) return '+' + text;
    if (n < 0) return '-' + text;
    return text;
}

export function formatMoney(value) {
    const n = toNumber(value) || 0;
    return '$ ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Clase de color de una diferencia: verde cuadra, azul sobra, rojo falta; sin
 *  diferencia (no contado), gris. */
export function diffClass(value) {
    const n = toNumber(value);
    if (n === null) return 'text-color-secondary';
    if (n === 0) return 'text-green-500 font-medium';
    return n > 0 ? 'text-blue-500 font-medium' : 'text-red-500 font-medium';
}

// Sentido de un ajuste junto a la cantidad (H70): el motivo («Diferencia sin explicar»,
// «Otro») no dice si faltó o sobró; solo se veía en el signo.
export function adjustmentDirection(value) {
    const n = toNumber(value);
    if (n === null || n === 0) return null;
    return n < 0 ? 'Faltante' : 'Sobrante';
}

export function formatDate(value) {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
        return value;
    }
}

/** ¿Es un físico declarable? Número ≥ 0 con hasta 3 decimales. */
export function isValidPhysical(value) {
    const n = toNumber(value);
    if (n === null || n < 0) return false;
    return Math.abs(Math.round(n * 1000) - n * 1000) < 1e-6;
}

function isDeclared(value) {
    return value !== null && value !== undefined && value !== '';
}

/** Cuántos productos lleva contados el usuario. */
export function countProgress(lines) {
    const total = (lines || []).length;
    const counted = (lines || []).filter(l => isDeclared(l.physicalStock)).length;
    return { counted, total };
}

/** Líneas que se envían al guardar el conteo: solo el físico, nunca otra cosa. */
export function buildCountPayload(lines) {
    return (lines || []).map(l => ({
        productId: l.productId,
        physicalStock: isDeclared(l.physicalStock) ? String(l.physicalStock) : null,
    }));
}

/** Agrupa los productos del conteo por categoría, en el orden en que llegan. */
export function groupByCategory(lines) {
    const groups = [];
    const index = {};
    for (const line of lines || []) {
        const name = line.categoryName || 'Sin categoría';
        if (!(name in index)) {
            index[name] = groups.length;
            groups.push({ name, lines: [] });
        }
        groups[index[name]].lines.push(line);
    }
    return groups;
}

// Borrador del conteo en el navegador: si se recarga la página no se pierde lo
// contado. Solo guarda los físicos declarados. El almacenamiento puede no estar
// disponible (modo privado, bloqueado): nunca rompe la pantalla.
export function draftKey(userId, activityId) {
    return `inventoryCountDraft:${userId || 'anon'}:${activityId || 'default'}`;
}

export function loadDraft(storage, key) {
    try {
        const raw = storage && storage.getItem(key);
        const data = raw ? JSON.parse(raw) : null;
        return data && typeof data === 'object' ? data : {};
    } catch {
        return {};
    }
}

export function saveDraft(storage, key, lines) {
    try {
        const values = {};
        for (const l of lines || []) {
            if (isDeclared(l.physicalStock)) values[l.productId] = l.physicalStock;
        }
        if (Object.keys(values).length === 0) storage.removeItem(key);
        else storage.setItem(key, JSON.stringify(values));
    } catch {
        // sin almacenamiento: el conteo sigue funcionando, solo sin borrador
    }
}

export function clearDraft(storage, key) {
    try {
        storage.removeItem(key);
    } catch {
        // idem
    }
}

// Lista de compras: el texto que se pega en WhatsApp para pedirle a cada proveedor.
// `quantities` son las cantidades que el admin editó, por productId; lo que no
// editó va con la sugerida. Lo que quede en cero no se pide.
export function buildShoppingText(providers, quantities = {}) {
    const blocks = [];
    for (const provider of providers || []) {
        const lines = [];
        for (const item of provider.items || []) {
            const raw = item.productId in quantities ? quantities[item.productId] : item.suggested;
            const n = toNumber(raw);
            if (n === null || n <= 0) continue;
            lines.push(`- ${formatQty(n)} × ${item.productName}`);
        }
        if (lines.length) blocks.push(`*${provider.providerName}*\n${lines.join('\n')}`);
    }
    return blocks.join('\n\n');
}
